import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useParams } from 'react-router';
import { BaseRouteParams, ROUTES } from 'pages/routes';
import { ApplicationState } from 'store/store';
import { Throbber } from './Throbber';
import { useAuth } from './useAuth';
import { setOnCloseRedirectUrl } from 'components/user/localAuth';
import { isString } from './typeInference';
import { setReturnUrl } from 'store/auth/authActions';
import { useQuery } from 'src/hooks/useQuery';
import { useLocalHistory } from './useLocalHistory';

export const UserLayout = ({ children }: React.PropsWithChildren<any>) => {
    const dispatch = useDispatch();
    const { isLoading } = useSelector((state: ApplicationState) => state.auth);
    const { getParsedPath } = useLocalHistory();
    const { t } = useTranslation();
    const { isGuest, isLoggedIn } = useAuth();
    const { merchantId } = useParams<BaseRouteParams>();
    const { pathname } = useLocation();
    const authJourney = useQuery('j');
    const isLoginRedirect = authJourney === 'login';
    React.useEffect(() => {
        // Set up redirect URL for after authentication
        if (!(!isGuest && isLoggedIn) && !isLoading && isString(merchantId)) {
            setOnCloseRedirectUrl(pathname, merchantId);
            setReturnUrl(pathname)(dispatch);
        }
    }, [dispatch, isGuest, isLoading, isLoggedIn, merchantId, pathname]);

    // If user is authenticated and not a guest, render the child routes
    if (!isGuest && isLoggedIn) {
        return children;
    }

    // Show loading state while auth is being checked
    if (isLoading) {
        return <Throbber text={t('GENERAL_LOADING')} />;
    }

    // Redirect to appropriate auth page
    if (isLoginRedirect) {
        return <Navigate to={getParsedPath(ROUTES.USER.LOGIN)} replace />;
    }
    return <Navigate to={getParsedPath(ROUTES.USER.REGISTER)} replace />;
};
